import { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Upload,
  Camera,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  ZoomIn,
  RotateCcw,
  ScanBarcode
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import BatchConfirmationGrid from './BatchConfirmationGrid';

/**
 * ShelfScanUpload - Phase 1A Component
 * 
 * Allows DRLPs to upload shelf/table photos and extract multiple barcodes at once.
 * Uses image preprocessing to maximize barcode detection rates.
 */
export default function ShelfScanUpload({ onComplete, onCancel }) {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  
  // State
  const [mode, setMode] = useState('upload'); // 'upload', 'preview', 'scanning', 'results'
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanResults, setScanResults] = useState(null);
  const [accumulatedBarcodes, setAccumulatedBarcodes] = useState([]); // Accumulate barcodes across multiple scans
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG, etc.)');
      return;
    }
    
    // Validate file size (15MB max)
    if (file.size > 15 * 1024 * 1024) {
      setError('Image too large. Maximum size is 15MB.');
      return;
    }
    
    setError(null);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMode('preview');
  }, []);

  // Handle drag and drop
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Reset file selection only (keeps accumulated barcodes for "Scan Another Shelf")
  const handleResetFile = useCallback(() => {
    setMode('upload');
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    setScanProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Full reset - clears everything including accumulated barcodes
  const handleFullReset = useCallback(() => {
    setMode('upload');
    setSelectedFile(null);
    setPreviewUrl(null);
    setScanResults(null);
    setAccumulatedBarcodes([]);
    setError(null);
    setScanProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Scan the image
  const handleScan = useCallback(async () => {
    if (!selectedFile) return;
    
    setScanning(true);
    setMode('scanning');
    setError(null);
    setScanProgress(10);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      setScanProgress(30);
      
      const response = await api.post('/barcode/scan-image-multi', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setScanProgress(30 + percentCompleted);
        }
      });
      
      setScanProgress(90);
      
      if (response.data.success) {
        const newBarcodes = response.data.barcodes || [];
        
        // Accumulate barcodes, avoiding duplicates by raw_code
        setAccumulatedBarcodes(prev => {
          const existingCodes = new Set(prev.map(b => b.raw_code));
          const uniqueNewBarcodes = newBarcodes.filter(b => !existingCodes.has(b.raw_code));
          const combined = [...prev, ...uniqueNewBarcodes];
          
          // Update scanResults with combined totals
          const readyCount = combined.filter(b => b.ready_to_post).length;
          const needsInputCount = combined.filter(b => b.needs_input).length;
          
          setScanResults({
            ...response.data,
            barcodes: combined,
            count: combined.length,
            ready_count: readyCount,
            needs_input_count: needsInputCount,
            accumulated: true
          });
          
          return combined;
        });
        
        setMode('results');
        
        const duplicatesSkipped = newBarcodes.length - (response.data.barcodes?.filter(b => 
          !accumulatedBarcodes.some(ab => ab.raw_code === b.raw_code)
        ).length || 0);
        
        if (duplicatesSkipped > 0) {
          toast.success(
            `Found ${newBarcodes.length} products (${duplicatesSkipped} duplicates skipped)`
          );
        } else {
          toast.success(
            `Found ${newBarcodes.length} products - added to batch`
          );
        }
      } else {
        setError(response.data.message || 'No barcodes detected');
        setMode(accumulatedBarcodes.length > 0 ? 'results' : 'preview');
      }
      
      setScanProgress(100);
      
    } catch (err) {
      console.error('Shelf scan error:', err);
      setError(err.response?.data?.detail || 'Failed to scan image. Please try again.');
      setMode(accumulatedBarcodes.length > 0 ? 'results' : 'preview');
    } finally {
      setScanning(false);
    }
  }, [selectedFile, accumulatedBarcodes]);

  // Submit batch to create RSHDs
  const handleBatchSubmit = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    
    setSubmitting(true);
    
    try {
      const response = await api.post('/barcode/batch-create-rshd', { items });
      
      if (response.data.success) {
        const { created, failed } = response.data.summary;
        
        if (created > 0) {
          toast.success(`Successfully posted ${created} products!`);
        }
        
        if (failed > 0) {
          toast.warning(`${failed} products could not be posted`);
        }
        
        // Call completion handler
        if (onComplete) {
          onComplete(response.data);
        }
        
        handleFullReset();
      }
    } catch (err) {
      console.error('Batch submit error:', err);
      toast.error(err.response?.data?.detail || 'Failed to post products');
    } finally {
      setSubmitting(false);
    }
  }, [onComplete, handleFullReset]);

  // Render upload zone
  const renderUploadZone = () => (
    <Card className="border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
      <CardContent className="pt-6">
        <div
          className="flex flex-col items-center justify-center py-12 cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          data-testid="shelf-upload-zone"
        >
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <Upload className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('shelf_scan.upload_title', 'Upload Shelf Photo')}
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-sm mb-4">
            {t('shelf_scan.upload_desc', 'Take a photo of your shelf or table with visible barcodes. We\'ll detect all products automatically.')}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ImageIcon className="w-4 h-4" />
            <span>JPEG, PNG • Max 15MB</span>
          </div>
          
          <Button className="mt-6" data-testid="select-image-btn">
            <Camera className="w-4 h-4 mr-2" />
            {t('shelf_scan.select_image', 'Select Image')}
          </Button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />
      </CardContent>
    </Card>
  );

  // Render preview
  const renderPreview = () => (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-600" />
            {t('shelf_scan.preview', 'Image Preview')}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={handleResetFile} data-testid="reset-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <CardDescription>
          {selectedFile?.name} • {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
          {accumulatedBarcodes.length > 0 && (
            <span className="ml-2 text-blue-600">
              • {accumulatedBarcodes.length} products already scanned
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative rounded-lg overflow-hidden bg-gray-100 mb-4">
          <img
            src={previewUrl}
            alt="Shelf preview"
            className="w-full max-h-[400px] object-contain"
            data-testid="preview-image"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <Button variant="secondary" size="icon" className="h-8 w-8">
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleResetFile} className="flex-1">
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('shelf_scan.choose_different', 'Choose Different')}
          </Button>
          <Button onClick={handleScan} disabled={scanning} className="flex-1" data-testid="scan-btn">
            <ScanBarcode className="w-4 h-4 mr-2" />
            {t('shelf_scan.scan_barcodes', 'Scan for Barcodes')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render scanning progress
  const renderScanning = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <ScanBarcode className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('shelf_scan.scanning', 'Scanning for Barcodes...')}
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
            {t('shelf_scan.scanning_desc', 'Analyzing image and extracting product barcodes')}
          </p>
          
          <div className="w-full max-w-xs">
            <Progress value={scanProgress} className="h-2" />
            <p className="text-xs text-gray-400 text-center mt-2">{scanProgress}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Render results
  const renderResults = () => {
    if (!scanResults || !scanResults.barcodes) {
      return null;
    }
    
    return (
      <div className="space-y-4">
        {/* Summary Header */}
        <Card className="bg-gradient-to-r from-blue-50 to-green-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {t('shelf_scan.scan_complete', 'Scan Complete')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {scanResults.count} products found {scanResults.accumulated && 'from multiple photos'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  {scanResults.ready_count} ready
                </Badge>
                {scanResults.needs_input_count > 0 && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                    {scanResults.needs_input_count} need input
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Batch Confirmation Grid */}
        <BatchConfirmationGrid
          items={scanResults.barcodes}
          onSubmit={handleBatchSubmit}
          onCancel={handleFullReset}
          loading={submitting}
          title={t('shelf_scan.review_products', 'Review & Post Products')}
          submitLabel={t('shelf_scan.post_selected', 'Post Selected')}
        />
        
        {/* Action buttons for scanning more */}
        <div className="flex items-center justify-center gap-4">
          <Button variant="outline" onClick={handleResetFile}>
            <Camera className="w-4 h-4 mr-2" />
            {t('shelf_scan.scan_another', 'Scan Another Shelf')}
          </Button>
          <Button variant="ghost" onClick={handleFullReset} className="text-gray-500">
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('shelf_scan.clear_all', 'Clear All & Start Over')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="shelf-scan-upload">
      {mode === 'upload' && renderUploadZone()}
      {mode === 'preview' && renderPreview()}
      {mode === 'scanning' && renderScanning()}
      {mode === 'results' && renderResults()}
    </div>
  );
}
